<template>
  <div class="settings-layout">
    <!-- Settings Header -->
    <div class="settings-header">
      <h1 class="page-title">
        <i class="bi bi-gear"></i>
        Ρυθμίσεις
      </h1>
    </div>

    <!-- Settings Navigation -->
    <div class="settings-nav">
      <nav class="custom-settings-navbar">
        <div class="nav-container">
          <!-- Mobile Toggle Button -->
          <button
            class="mobile-nav-toggle"
            @click="toggleMobileNav"
            :aria-expanded="mobileNavOpen"
          >
            <i :class="mobileNavOpen ? 'bi bi-x-lg' : 'bi bi-list'"></i>
            <span class="toggle-label">{{ mobileNavOpen ? 'Κλείσιμο' : 'Μενού Ρυθμίσεων' }}</span>
          </button>

          <div class="nav-items-wrapper" :class="{ 'mobile-collapsed': !mobileNavOpen }" @click="handleNavLinkClick">
            <router-link
              :to="{ name: 'admin-settings-content' }"
              class="nav-link"
              :class="{ active: $route.name === 'admin-settings-content' }"
            >
              <i class="bi bi-text-paragraph"></i>
              Κείμενα
            </router-link>
            <router-link
              :to="{ name: 'admin-settings-gallery' }"
              class="nav-link"
              :class="{ active: $route.name === 'admin-settings-gallery' }"
            >
              <i class="bi bi-images"></i>
              Gallery Φωτογραφιών
            </router-link>
            <router-link
              :to="{ name: 'admin-settings-masonry' }"
              class="nav-link"
              :class="{ active: $route.name === 'admin-settings-masonry' }"
            >
              <i class="bi bi-grid-3x3-gap"></i>
              Masonry Gallery
            </router-link>
            <router-link
              :to="{ name: 'admin-settings-layout' }"
              class="nav-link"
              :class="{ active: $route.name === 'admin-settings-layout' }"
            >
              <i class="bi bi-layout-wtf"></i>
              Layout
            </router-link>
            <router-link
              :to="{ name: 'admin-settings-sidebar' }"
              class="nav-link"
              :class="{ active: $route.name === 'admin-settings-sidebar' }"
            >
              <i class="bi bi-layout-sidebar-inset"></i>
              Sidebar
            </router-link>
            <router-link
              :to="{ name: 'admin-settings-navigation' }"
              class="nav-link"
              :class="{ active: $route.name === 'admin-settings-navigation' }"
            >
              <i class="bi bi-layout-text-sidebar-reverse"></i>
              Navigation
            </router-link>
            <router-link
              :to="{ name: 'admin-settings-footer' }"
              class="nav-link"
              :class="{ active: $route.name === 'admin-settings-footer' }"
            >
              <i class="bi bi-layout-text-window-reverse"></i>
              Footer
            </router-link>
            <router-link
              :to="{ name: 'admin-settings-system' }"
              class="nav-link"
              :class="{ active: $route.name === 'admin-settings-system' }"
            >
              <i class="bi bi-cpu"></i>
              Σύστημα
            </router-link>
            <router-link
              :to="{ name: 'admin-settings-ssl' }"
              class="nav-link"
              :class="{ active: $route.name === 'admin-settings-ssl' }"
            >
              <i class="bi bi-shield-lock"></i>
              SSL
            </router-link>

            <router-link
              :to="{ name: 'admin-settings-email' }"
              class="nav-link"
              :class="{ active: $route.name === 'admin-settings-email' }"
            >
              <i class="bi bi-envelope-gear"></i>
              Email
            </router-link>
            <router-link
              :to="{ name: 'admin-settings-contact' }"
              class="nav-link"
              :class="{ active: $route.name === 'admin-settings-contact' }"
            >
              <i class="bi bi-telephone-plus"></i>
              Επικοινωνία
            </router-link>
            <router-link
              :to="{ name: 'admin-settings-seo' }"
              class="nav-link"
              :class="{ active: $route.name === 'admin-settings-seo' }"
            >
              <i class="bi bi-search"></i>
              SEO & Schema.org
            </router-link>
            <router-link
              :to="{ name: 'admin-settings-blog' }"
              class="nav-link"
              :class="{ active: $route.name === 'admin-settings-blog' }"
            >
              <i class="bi bi-journal-text"></i>
              Blog
            </router-link>
            <router-link
              :to="{ name: 'admin-settings-facebook-auto-post' }"
              class="nav-link"
              :class="{ active: $route.name === 'admin-settings-facebook-auto-post' }"
            >
              <i class="bi bi-facebook"></i>
              Facebook Auto-Post
            </router-link>
            <router-link
              :to="{ name: 'admin-settings-instagram' }"
              class="nav-link"
              :class="{ active: $route.name === 'admin-settings-instagram' }"
            >
              <i class="bi bi-instagram"></i>
              Instagram
            </router-link>
          </div>
        </div>
      </nav>
    </div>

    <!-- Settings Content -->
    <div class="settings-content">
      <router-view />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

// Mobile navigation state
const mobileNavOpen = ref(false)

// Toggle mobile navigation
const toggleMobileNav = () => {
  mobileNavOpen.value = !mobileNavOpen.value
}

// Close mobile nav when a link is clicked
const handleNavLinkClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (target.closest('a')) {
    mobileNavOpen.value = false
  }
}
</script>

<style scoped>
/* ── Settings Layout ───────────────────────────────────────────────────── */
.settings-layout {
  display: flex;
  flex-direction: column;
}

/* Header */
.settings-header {
  text-align: center;
  padding: .75rem 0 .5rem;
  margin-bottom: .75rem;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -.02em;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: .5rem;
}

.page-title i { color: #2563eb; }

/* Navigation wrap */
.settings-nav { margin-bottom: 1.5rem; }

/* Navbar */
.custom-settings-navbar {
  background: linear-gradient(135deg, #1e3a5f 0%, #1d4ed8 100%);
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(37, 99, 235, .25);
  padding: .875rem 1.5rem;
}

.nav-container { width: 100%; }

/* Mobile toggle — hidden by default */
.mobile-nav-toggle {
  display: none;
  width: 100%;
  background: rgba(255,255,255,.12);
  border: 1.5px solid rgba(255,255,255,.25);
  color: #fff;
  padding: .75rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: .875rem;
  cursor: pointer;
  align-items: center;
  gap: .5rem;
  margin-bottom: .75rem;
  transition: background .2s;
  font-family: inherit;
}

.mobile-nav-toggle:hover { background: rgba(255,255,255,.22); }

.toggle-label { flex: 1; text-align: left; }

/* Nav items row */
.nav-items-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: .4rem;
}

/* Nav links */
.settings-nav .nav-link {
  display: inline-flex;
  align-items: center;
  gap: .35rem;
  padding: .45rem .85rem;
  font-size: .8rem;
  font-weight: 600;
  color: rgba(255,255,255,.9);
  background: rgba(255,255,255,.1);
  border: 1.5px solid rgba(255,255,255,.18);
  border-radius: 8px;
  text-decoration: none;
  white-space: nowrap;
  transition: background .18s, border-color .18s, transform .15s, box-shadow .18s;
}

.settings-nav .nav-link:hover {
  background: rgba(255,255,255,.2);
  border-color: rgba(255,255,255,.38);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(255,255,255,.12);
}

.settings-nav .nav-link.active {
  background: #fff;
  color: #1d4ed8;
  border-color: #fff;
  font-weight: 700;
  box-shadow: 0 4px 14px rgba(255,255,255,.25);
}

.settings-nav .nav-link.active:hover {
  background: #f1f5f9;
  color: #1d4ed8;
  transform: translateY(-1px);
}

.settings-nav .nav-link i { font-size: .9rem; }

/* Content area */
.settings-content { flex: 1; }

/* ── Responsive ─────────────────────────────────────────────────────────── */
@media (max-width: 900px) {
  .custom-settings-navbar { padding: .75rem 1rem; }
}

@media (max-width: 768px) {
  .mobile-nav-toggle { display: flex; }

  .nav-items-wrapper {
    flex-direction: column;
    gap: .35rem;
    max-height: 1000px;
    overflow: hidden;
    opacity: 1;
    transition: max-height .4s ease, opacity .3s ease;
  }

  .nav-items-wrapper.mobile-collapsed {
    max-height: 0;
    opacity: 0;
  }

  .settings-nav .nav-link {
    width: 100%;
    justify-content: center;
    padding: .75rem 1rem;
    font-size: .875rem;
  }

  .settings-nav .nav-link:hover { transform: none; }
}

@media (max-width: 576px) {
  .page-title { font-size: 1.25rem; }

  .custom-settings-navbar {
    padding: .6rem .75rem;
    border-radius: 12px;
  }

  .settings-nav .nav-link {
    padding: .65rem .875rem;
    font-size: .8rem;
  }
}
</style>
